import React from 'react';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import axios from 'axios';
import Fuse from 'fuse.js';
import { NotificationManager } from 'react-notifications';
import { withRouter } from 'react-router-dom';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import EnhancedTableToolbar from './EnhancedTableToolbar';
import EnhancedTableHead from './EnhancedTableHead';
import LocationReviewDataRow from './LocationReviewDataRow';
import ReviewModalContent from './Modal/ReviewModalContent';
import ConnectGoogleAccount from '../../add_accounts/ConnectGoogleAccount';

function getSorting(order, orderBy) {
	if (orderBy === 'name' || orderBy === 'province') {
		return order === 'desc'
			? (a, b) => {
					if (a[orderBy] && b[orderBy]) {
						return String(a[orderBy]).localeCompare(
							String(b[orderBy])
						);
					}
					return 0;
			  }
			: (a, b) => {
					if (a[orderBy] && b[orderBy]) {
						return String(b[orderBy]).localeCompare(
							String(a[orderBy])
						);
					}
					return 0;
			  };
	}

	if (orderBy === 'rating' || orderBy === 'totalReviewCount') {
		return order === 'desc'
			? (a, b) => {
					const aChecked = a.google[orderBy] || '0';
					const bChecked = b.google[orderBy] || '0';

					if (
						typeof aChecked === 'string' &&
						typeof bChecked === 'string'
					) {
						return (
							Number(bChecked.replace(/\D/g, '')) -
							Number(aChecked.replace(/\D/g, ''))
						);
					}
					return Number(bChecked) - Number(aChecked);
			  }
			: (a, b) => {
					const aChecked = a.google[orderBy] || '0';
					const bChecked = b.google[orderBy] || '0';
					if (
						typeof aChecked === 'string' &&
						typeof bChecked === 'string'
					) {
						return (
							Number(aChecked.replace(/\D/g, '')) -
							Number(bChecked.replace(/\D/g, ''))
						);
					}
					return Number(aChecked) - Number(bChecked);
			  };
	}

	if (
		orderBy === 'directQueries' ||
		orderBy === 'indirectQueries' ||
		orderBy === 'websiteActions' ||
		orderBy === 'phoneActions' ||
		orderBy === 'mapsViews'
	) {
		return order === 'desc'
			? (a, b) => {
					const aChecked = a.google.insights[orderBy] || '0';
					const bChecked = b.google.insights[orderBy] || '0';

					if (
						typeof aChecked === 'string' &&
						typeof bChecked === 'string'
					) {
						return (
							Number(bChecked.replace(/\D/g, '')) -
							Number(aChecked.replace(/\D/g, ''))
						);
					}
					return Number(bChecked) - Number(aChecked);
			  }
			: (a, b) => {
					const aChecked = a.google.insights[orderBy] || '0';
					const bChecked = b.google.insights[orderBy] || '0';
					if (
						typeof aChecked === 'string' &&
						typeof bChecked === 'string'
					) {
						return (
							Number(aChecked.replace(/\D/g, '')) -
							Number(bChecked.replace(/\D/g, ''))
						);
					}
					return Number(aChecked) - Number(bChecked);
			  };
	}

	// Default sorting function for any other fields
	return order === 'desc'
		? (a, b) => (a[orderBy] > b[orderBy] ? -1 : 1)
		: (a, b) => (a[orderBy] > b[orderBy] ? 1 : -1);
}

const styles = (theme) => ({
	root: {
		width: '100%',
		marginTop: theme.spacing.unit * 3,
		marginBottom: theme.spacing.unit * 3,
		boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
		borderRadius: '12px',
		overflow: 'hidden',
		backgroundColor: '#ffffff',
	},
	table: {
		width: '100%',
		'& thead th': {
			fontWeight: 500,
			fontSize: '0.75rem',
			color: theme.palette.text.secondary,
			backgroundColor: '#f8fafc',
			padding: '12px 8px',
			borderBottom: `2px solid ${theme.palette.grey[200]}`,
			textTransform: 'uppercase',
			letterSpacing: '0.03em',
			lineHeight: 1.2,
			whiteSpace: 'nowrap',
			'&:hover': {
				backgroundColor: '#f1f5f9',
			},
			'&[data-column="name"]': {
				minWidth: '180px',
				paddingLeft: 16,
			},
			'&[data-column="province"]': {
				width: '80px',
			},
			'&[data-column="rating"]': {
				width: '80px',
			},
			'&[data-column="totalReviewCount"]': {
				width: '100px',
			},
			'&[data-column="desktopSearchViews"]': {
				width: '100px',
			},
			'&[data-column="mobileSearchViews"]': {
				width: '100px',
			},
			'&[data-column="websiteActions"]': {
				width: '90px',
			},
			'&[data-column="mapsViews"]': {
				width: '80px',
			},
			'&[data-column="phoneActions"]': {
				width: '80px',
			},
			'@media (max-width: 1280px)': {
				'&[data-column="desktopSearchViews"], &[data-column="mobileSearchViews"]':
					{
						display: 'none',
					},
			},
			'@media (max-width: 960px)': {
				'&[data-column="websiteActions"], &[data-column="mapsViews"], &[data-column="phoneActions"]':
					{
						display: 'none',
					},
			},
		},
		'& tbody td, & tbody th': {
			padding: '12px 8px',
			fontSize: '0.875rem',
			color: theme.palette.text.secondary,
			borderBottom: `1px solid ${theme.palette.grey[100]}`,
			verticalAlign: 'middle',
			'&[data-column="name"]': {
				fontWeight: 500,
				color: theme.palette.text.primary,
				paddingLeft: 16,
			},
			'@media (max-width: 1280px)': {
				'&[data-column="desktopSearchViews"], &[data-column="mobileSearchViews"]':
					{
						display: 'none',
					},
			},
			'@media (max-width: 960px)': {
				'&[data-column="websiteActions"], &[data-column="mapsViews"], &[data-column="phoneActions"]':
					{
						display: 'none',
					},
			},
		},
		'& tbody tr': {
			transition: 'all 0.2s ease',
			'&:hover': {
				backgroundColor: '#f0f4fa',
				transform: 'translateY(-1px)',
				boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
				cursor: 'pointer',
			},
		},
	},
	tableWrapper: {
		overflowX: 'auto',
		'&::-webkit-scrollbar': {
			height: '6px',
			width: '6px',
		},
		'&::-webkit-scrollbar-track': {
			background: '#f8fafc',
			borderRadius: '3px',
		},
		'&::-webkit-scrollbar-thumb': {
			background: theme.palette.grey[300],
			borderRadius: '3px',
			'&:hover': {
				background: theme.palette.grey[400],
			},
		},
		'-webkit-overflow-scrolling': 'touch',
		'scrollbar-width': 'thin',
	},
	exportContainer: {
		display: 'flex',
		alignItems: 'center',
		padding: '16px 24px',
		borderTop: `1px solid ${theme.palette.grey[100]}`,
		backgroundColor: '#f8fafc',
	},
	exportButton: {
		backgroundColor: theme.palette.secondary.main,
		color: '#ffffff',
		minWidth: '120px',
		marginRight: theme.spacing.unit * 4,
		textTransform: 'none',
		fontWeight: 500,
		padding: '8px 20px',
		borderRadius: '8px',
		boxShadow: 'none',
		'&:hover': {
			backgroundColor: theme.palette.secondary.dark,
			boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
		},
	},
	exportMessage: {
		color: theme.palette.text.secondary,
		fontSize: '0.875rem',
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.unit,
	},
	downloadLink: {
		color: theme.palette.secondary.main,
		textDecoration: 'none',
		fontWeight: 500,
		marginLeft: theme.spacing.unit,
		transition: 'all 0.2s ease',
		'&:hover': {
			color: theme.palette.secondary.dark,
			textDecoration: 'underline',
		},
	},
	loadingContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.unit,
	},
	progress: {
		margin: theme.spacing.unit * 2,
		color: theme.palette.secondary.main,
	},
	pagination: {
		borderTop: `1px solid ${theme.palette.grey[100]}`,
		padding: '16px 24px',
		'& .MuiTablePagination-select': {
			borderRadius: '6px',
			marginRight: theme.spacing.unit,
			marginLeft: theme.spacing.unit,
		},
	},
	button: {
		marginLeft: theme.spacing.unit * 2,
		marginTop: theme.spacing.unit * 1.5,
	},
	input: {
		display: 'none',
	},
	paper: {
		position: 'absolute',
		width: theme.spacing.unit * 50,
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[5],
		padding: theme.spacing.unit * 4,
	},
	loading: {
		margin: theme.spacing.unit * 2,
	},
	exportError: {
		color: '#f44336',
		marginLeft: '16px',
	},
	exportWarning: {
		color: '#ff9800',
		marginTop: '8px',
		fontSize: '0.875rem',
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		background: 'rgba(255,255,255,0.7)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2,
	},
});

class EnhancedTable extends React.Component {
	getProvinceHeaderLabel = (locations) => {
		const CANADA = new Set([
			'AB',
			'BC',
			'MB',
			'NB',
			'NL',
			'NS',
			'NT',
			'NU',
			'ON',
			'PE',
			'QC',
			'SK',
			'YT',
		]);
		const US = new Set([
			'AL',
			'AK',
			'AZ',
			'AR',
			'CA',
			'CO',
			'CT',
			'DE',
			'FL',
			'GA',
			'HI',
			'ID',
			'IL',
			'IN',
			'IA',
			'KS',
			'KY',
			'LA',
			'ME',
			'MD',
			'MA',
			'MI',
			'MN',
			'MS',
			'MO',
			'MT',
			'NE',
			'NV',
			'NH',
			'NJ',
			'NM',
			'NY',
			'NC',
			'ND',
			'OH',
			'OK',
			'OR',
			'PA',
			'RI',
			'SC',
			'SD',
			'TN',
			'TX',
			'UT',
			'VT',
			'VA',
			'WA',
			'WV',
			'WI',
			'WY',
			'DC',
		]);

		const codes = (Array.isArray(locations) ? locations : [])
			.map((l) => (l && l.province ? String(l.province).trim() : ''))
			.filter(Boolean)
			.map((p) => p.toUpperCase());

		if (!codes.length) return 'State/Province';

		const hasUSOnly = codes.some((c) => US.has(c) && !CANADA.has(c));
		const hasCanadaOnly = codes.some((c) => CANADA.has(c) && !US.has(c));

		if (hasUSOnly && !hasCanadaOnly) return 'State';
		if (hasCanadaOnly && !hasUSOnly) return 'Province';
		return 'State/Province';
	};

	constructor(props) {
		super(props);

		this.state = {
			order: 'desc',
			orderBy: 'name',
			open: false,
			selected: [],
			data: [],
			showInaccessible: false,
			downloadUrl: null,
			exportMessage: null,
			loadingExportData: false,
			page: 0,
			rowsPerPage: 100,
			searchTerm: '',
			loading: true,
			loadingSingleExportData: false,
			singleExportMessage: '',
			singleExportUrl: '',
			authError: false,
			noGoogleAuth: false,
			_selectedEnterpriseLocation: null,
			selectedEnterpriseLocationName: '',
			exportError: null,
			exportWarnings: null,
		};
	}

	async componentDidMount() {
		try {
			const res = await axios.post('/api/load-all-review-table-data');

			const { enterpriseLocationData, error, requiresReauth } = res.data;

			if (error) {
				if (requiresReauth) {
					this.setState({
						loading: false,
						authError: true,
						noGoogleAuth: false,
					});
					NotificationManager.error(
						'Your Google authentication has expired. Please reconnect your Google account.',
						'Authentication Error',
						5000
					);
				} else if (!this.props.auth.googleRefresh) {
					this.setState({
						loading: false,
						authError: true,
						noGoogleAuth: true,
					});
					NotificationManager.error(
						'Please connect your Google My Business account to view your reviews.',
						'Setup Required',
						5000
					);
				} else {
					NotificationManager.error(
						'There was an error loading the review data. Please try again later.',
						'Error',
						5000
					);
					this.setState({ loading: false });
				}
				return;
			}

			this.setState({
				data: enterpriseLocationData || [],
				rowsPerPage: enterpriseLocationData
					? enterpriseLocationData.length
					: 100,
				loading: false,
			});
		} catch (err) {
			console.error('Error loading review data:', err);
			NotificationManager.error(
				'There was an error loading the review data. Please try again later.',
				'Error',
				5000
			);
			this.setState({ loading: false });
		}
	}

	searchData = (event) => {
		const searchTerm = event.target.value;
		this.setState({ searchTerm });
	};

	isInaccessibleLocation = (location) => {
		const status = location && location.google && location.google.error
			? location.google.error.status
			: null;
		const code = location && location.google && location.google.error
			? location.google.error.code
			: null;
		return (
			status === 'PERMISSION_DENIED' ||
			status === 'NOT_FOUND' ||
			code === 403 ||
			code === 404
		);
	};

	getVisibleData = () => {
		const { data, showInaccessible } = this.state;
		if (showInaccessible) return data;
		return (data || []).filter((l) => !this.isInaccessibleLocation(l));
	};

	handleClose = () => {
		this.setState({
			open: false,
			loadingSingleExportData: false,
			singleExportMessage: '',
			singleExportUrl: '',
		});
	};

	exportData = async () => {
		this.setState({
			exportMessage: 'We are creating a csv file for you, please wait.',
			loadingExportData: true,
			downloadUrl: '',
			exportError: null,
			exportWarnings: null,
		});

		try {
			const res = await axios.get('/api/export-review-table-data');
			const { data } = res;

			if (data.error) {
				this.setState({
					exportMessage: '',
					exportError:
						data.message ||
						'There was an error exporting your data.',
					loadingExportData: false,
				});
				NotificationManager.error(
					data.message || 'There was an error exporting your data.',
					'Export Failed',
					5000
				);
				return;
			}

			const { downloadUrl, warnings } = data;

			if (warnings) {
				NotificationManager.warning(
					warnings.message + '\n' + warnings.details.join('\n'),
					'Export Warning',
					10000
				);
			}

			this.setState({
				exportMessage:
					'Click the following link to download your data.',
				downloadUrl,
				loadingExportData: false,
				exportWarnings: warnings,
			});
		} catch (error) {
			const errorMessage =
				error.response &&
				error.response.data &&
				error.response.data.message
					? error.response.data.message
					: 'There was an error exporting your data.';
			this.setState({
				exportMessage: '',
				exportError: errorMessage,
				loadingExportData: false,
			});
			NotificationManager.error(errorMessage, 'Export Failed', 5000);
		}
	};

	exportSingleLocationData = async () => {
		this.setState({
			singleExportMessage: 'We are preparing your csv file, please wait.',
			loadingSingleExportData: true,
		});

		const { _selectedEnterpriseLocation } = this.state;

		const res = await axios.post('/api/export-single-location-data', {
			_enterpriseLocation: _selectedEnterpriseLocation,
		});

		const { data } = res;

		if (!data || data.error) {
			this.setState({
				singleExportMessage: 'There was an error exporting your data.',
				loadingSingleExportData: false,
			});
		}

		const { downloadUrl } = data;

		return this.setState({
			singleExportMessage: '',
			singleExportUrl: downloadUrl,
			loadingSingleExportData: false,
		});
	};

	handleRequestSort = (event, property) => {
		const orderBy = property;
		let order = 'desc';

		if (this.state.orderBy === property && this.state.order === 'desc') {
			order = 'asc';
		}

		this.setState({ order, orderBy });
	};

	handleClick = (event, id, name) => {
		this.setState({
			open: true,
			_selectedEnterpriseLocation: id,
			selectedEnterpriseLocationName: name,
		});
	};

	handleChangePage = (event, page) => {
		this.setState({ page });
	};

	handleChangeRowsPerPage = (event) => {
		this.setState({ rowsPerPage: event.target.value });
	};

	isSelected = (id) => this.state.selected.indexOf(id) !== -1;

	renderExportButton = (hasData = true) => {
		const { classes } = this.props;
		const {
			loadingExportData,
			exportMessage,
			downloadUrl,
			exportError,
			exportWarnings,
		} = this.state;

		if (loadingExportData) {
			return (
				<div className={classes.exportContainer}>
					<div className={classes.loadingContainer}>
						<CircularProgress
							className={classes.progress}
							size={20}
						/>
						<Typography className={classes.exportMessage}>
							{exportMessage}
						</Typography>
					</div>
				</div>
			);
		}

		if (!hasData) return null;

		return (
			<div className={classes.exportContainer}>
				<Button
					size='medium'
					variant='contained'
					className={classes.exportButton}
					onClick={this.exportData}
				>
					Export Data
				</Button>
				{exportError && (
					<Typography
						component='div'
						className={classes.exportError}
						style={{ color: '#f44336', marginLeft: '16px' }}
					>
						{exportError}
					</Typography>
				)}
				{downloadUrl && (
					<Typography
						component='div'
						className={classes.exportMessage}
					>
						{exportMessage}
						<a href={downloadUrl} className={classes.downloadLink}>
							Download
						</a>
					</Typography>
				)}
				{exportWarnings && (
					<Typography
						component='div'
						className={classes.exportWarning}
						style={{
							color: '#ff9800',
							marginTop: '8px',
							fontSize: '0.875rem',
						}}
					>
						{exportWarnings.message}
						<ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
							{exportWarnings.details.map((detail) => (
								<li key={`warning-${detail}`}>{detail}</li>
							))}
						</ul>
					</Typography>
				)}
			</div>
		);
	};

	renderEmptyStateRow = ({ title, subtitle }) => (
		<TableRow>
			<TableCell colSpan={9} style={{ padding: '28px 16px' }}>
				<div style={{ textAlign: 'center' }}>
					<Typography
						style={{
							fontWeight: 600,
							color: '#0f172a',
							marginBottom: 6,
						}}
					>
						{title}
					</Typography>
					<Typography style={{ color: '#64748b' }}>
						{subtitle}
					</Typography>
				</div>
			</TableCell>
		</TableRow>
	);

	renderLocationReviewDataRow = ({ location, isSelected }) => (
		<LocationReviewDataRow
			key={location._id}
			handleClick={this.handleClick}
			isSelected={isSelected}
			_enterpriseLocation={location._id}
			location={location}
		/>
	);

	renderSearchedTableBody = () => {
		const { order, orderBy, rowsPerPage, page, searchTerm } = this.state;
		const data = this.getVisibleData();

		const options = {
			shouldSort: true,
			threshold: 0.3,
			location: 0,
			distance: 10,
			maxPatternLength: 32,
			minMatchCharLength: 2,
			keys: ['name', 'province'],
		};

		const searchedData = new Fuse(data, options);

		return searchedData
			.search(searchTerm)
			.sort(getSorting(order, orderBy))
			.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			.map((location) => {
				const isSelected = this.isSelected(location._id);
				return this.renderLocationReviewDataRow({
					location,
					isSelected,
				});
			});
	};

	renderTableBody = () => {
		const { order, orderBy, rowsPerPage, page } = this.state;
		const data = this.getVisibleData();

		return data
			.sort(getSorting(order, orderBy))
			.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
			.map((location) => {
				const isSelected = this.isSelected(location._id);
				return this.renderLocationReviewDataRow({
					location,
					isSelected,
				});
			});
	};

	renderDownloadUrl = () => {
		const { singleExportMessage, singleExportUrl } = this.state;

		if (singleExportMessage) {
			return <Typography>{singleExportMessage}</Typography>;
		}

		if (singleExportUrl) {
			return (
				<Typography>
					Download: <a href={singleExportUrl}>Click here</a>
				</Typography>
			);
		}

		return null;
	};

	renderSkeletonRow = (rowIdx) => {
		const skeletonStyle = {
			background:
				'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%)',
			borderRadius: 4,
			minHeight: 24,
			height: 24,
			animation: 'skeleton-loading 1.2s infinite',
			backgroundSize: '400% 100%',
			margin: 0,
		};
		// Using rowIdx as key is acceptable for loading skeletons
		// eslint-disable-next-line react/no-array-index-key
		return (
			<TableRow key={`skeleton-${rowIdx}`}>
				{[...Array(9)].map((_, colIdx) => (
					// eslint-disable-next-line react/no-array-index-key
					<TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`}>
						<div style={skeletonStyle} />
					</TableCell>
				))}
			</TableRow>
		);
	};

	render() {
		const { classes } = this.props;
		const {
			data,
			order,
			orderBy,
			selected,
			rowsPerPage,
			page,
			loading,
			authError,
			noGoogleAuth,
			searchTerm,
			showInaccessible,
			_selectedEnterpriseLocation,
			selectedEnterpriseLocationName,
			loadingSingleExportData,
		} = this.state;
		const provinceLabel = this.getProvinceHeaderLabel(data);
		const inaccessibleCount = (data || []).filter((l) =>
			this.isInaccessibleLocation(l)
		).length;
		const visibleData = this.getVisibleData();

		let tableBodyContent;
		if (loading) {
			tableBodyContent = Array.from({ length: 10 }).map((_, idx) =>
				this.renderSkeletonRow(idx)
			);
		} else if (searchTerm) {
			tableBodyContent = this.renderSearchedTableBody();
		} else {
			tableBodyContent = this.renderTableBody();
		}

		if (!loading && (!tableBodyContent || tableBodyContent.length === 0)) {
			if (searchTerm) {
				tableBodyContent = [
					this.renderEmptyStateRow({
						title: 'No results',
						subtitle: `No locations match "${searchTerm}". Try a different search.`,
					}),
				];
			} else if (inaccessibleCount > 0 && !showInaccessible) {
				tableBodyContent = [
					this.renderEmptyStateRow({
						title: 'No accessible locations to show',
						subtitle:
							'All locations are hidden because Chekkit no longer has access. Use “Show” below to view them.',
					}),
				];
			} else {
				tableBodyContent = [
					this.renderEmptyStateRow({
						title: 'No locations yet',
						subtitle:
							'Once you add locations, your reviews and insights will show up here.',
					}),
				];
			}
		}

		if (authError) {
			return (
				<Paper
					className={classes.root}
					style={{
						width: '100%',
						margin: '24px auto',
						backgroundColor: '#fff',
					}}
				>
					<div style={{ padding: '32px', textAlign: 'center' }}>
						<Typography
							variant='h4'
							style={{
								marginBottom: 32,
								color: '#1a1a1a',
								fontWeight: 600,
								letterSpacing: '-0.5px',
								fontSize: '2.125rem',
							}}
						>
							{noGoogleAuth
								? 'View Your Customer Reviews'
								: 'Reconnect Your Google Account'}
						</Typography>
						<Typography
							style={{
								color: '#5f6368',
								fontSize: '1.15rem',
								lineHeight: 1.6,
								maxWidth: 600,
								margin: '0 auto 32px',
							}}
						>
							{noGoogleAuth
								? 'Connect your Google My Business account to monitor and respond to your customer reviews across all locations.'
								: 'To continue viewing your reviews and insights, please reconnect your Google account below.'}
						</Typography>
						<ConnectGoogleAccount />
					</div>
				</Paper>
			);
		}

		return (
			<Paper className={classes.root} elevation={0}>
				<EnhancedTableToolbar
					numSelected={selected.length}
					searchData={this.searchData}
					loading={loading}
				/>
				<div className={classes.tableWrapper}>
					<Table
						className={classes.table}
						aria-labelledby='tableTitle'
					>
						<EnhancedTableHead
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onRequestSort={this.handleRequestSort}
							rowCount={visibleData.length}
							provinceLabel={provinceLabel}
						/>
						<TableBody>{tableBodyContent}</TableBody>
					</Table>
				</div>
				{this.renderExportButton(visibleData.length > 0)}
				{!loading && inaccessibleCount > 0 ? (
					<div
						style={{
							padding: '10px 16px',
							borderTop: '1px solid #f3f4f6',
							borderBottom: '1px solid #f3f4f6',
							backgroundColor: '#fff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 12,
							flexWrap: 'wrap',
						}}
					>
						<Typography
							style={{ fontSize: '0.9rem', color: '#5f6368' }}
						>
							{showInaccessible
								? `Showing ${inaccessibleCount} location${
										inaccessibleCount === 1 ? '' : 's'
								  } that Chekkit no longer has access to.`
								: `Hiding ${inaccessibleCount} location${
										inaccessibleCount === 1 ? '' : 's'
								  } that Chekkit no longer has access to.`}
						</Typography>
						<Button
							variant='contained'
							color='primary'
							size='small'
							disableElevation
							style={{
								textTransform: 'none',
								fontWeight: 500,
							}}
							onClick={() =>
								this.setState({
									showInaccessible: !showInaccessible,
									page: 0,
								})
							}
						>
							{showInaccessible ? 'Hide' : 'Show'}
						</Button>
					</div>
				) : null}
				<div className={classes.pagination}>
					<TablePagination
						component='div'
						count={visibleData.length}
						rowsPerPage={rowsPerPage}
						rowsPerPageOptions={[50, 100, 150]}
						page={page}
						backIconButtonProps={{
							'aria-label': 'Previous Page',
						}}
						nextIconButtonProps={{
							'aria-label': 'Next Page',
						}}
						onChangePage={this.handleChangePage}
						onChangeRowsPerPage={this.handleChangeRowsPerPage}
					/>
				</div>

				<Dialog
					open={this.state.open}
					keepMounted
					maxWidth={false}
					scroll='paper'
					onClose={this.handleClose}
					aria-labelledby='alert-dialog-slide-title'
					aria-describedby='alert-dialog-slide-description'
					PaperProps={{ style: { minWidth: 900, minHeight: 500 } }}
				>
					<DialogTitle id='alert-dialog-slide-title'>
						{selectedEnterpriseLocationName || 'Latest Reviews'}
					</DialogTitle>
					<DialogContent style={{ paddingBottom: 0 }}>
						<ReviewModalContent
							_enterpriseLocation={_selectedEnterpriseLocation}
						/>
					</DialogContent>
					<DialogActions>
						{loadingSingleExportData ? (
							<CircularProgress
								className={classes.progress}
								size={20}
							/>
						) : null}
						{this.renderDownloadUrl()}
						<Button
							onClick={this.exportSingleLocationData}
							color='secondary'
							className={classes.exportButton}
						>
							Export Data
						</Button>
						<Button onClick={this.handleClose} color='primary'>
							Close
						</Button>
					</DialogActions>
				</Dialog>
			</Paper>
		);
	}
}

export default withRouter(withStyles(styles)(EnhancedTable));



// WEBPACK FOOTER //
// ./src/components/review/ReviewTable/ReviewTable.js